/**
 * Session-aware fetch wrapper.
 *
 * On a 401, automatically attempts a single token refresh via
 * POST /api/auth/refresh. If the refresh succeeds the original request
 * is retried once. If the refresh itself fails the registered
 * sessionExpired handler fires and a SessionExpiredError is thrown.
 */

let onSessionExpired: (() => void) | null = null;

export function setSessionExpiredHandler(handler: () => void) {
  onSessionExpired = handler;
}

export class SessionExpiredError extends Error {
  constructor(message = 'Session expired') {
    super(message);
    this.name = 'SessionExpiredError';
  }
}

export async function apiFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  let response = await fetch(input, init);

  if (response.status === 401) {
    // Attempt a single token refresh
    const refreshResponse = await fetch('/api/auth/refresh', { method: 'POST' });

    if (refreshResponse.ok) {
      // Retry original request with (now-refreshed) session cookie
      response = await fetch(input, init);
    } else {
      // Session is truly gone
      onSessionExpired?.();
      throw new SessionExpiredError();
    }
  }

  return response;
}
